// ── CANCEL ITEM MODAL ─────────────────────────────────────────────────────────
// Universal cancel-with-credit flow. Writes ledger entries (client side and
// vendor side independently) and flips item.status to 'Cancelled'.
//
// Three entry points feed this modal: Item detail page, Vendor Payments PO
// drilldown, Client Invoices Orders in Process row. The modal is the same
// regardless of where it's opened from — just pre-selects the item.
//
// Original invoices and vendor_payments rows are NEVER edited. We only
// write new ledger entries that net the effect. That's the audit trail.

import React, { useState, useMemo } from 'react';
import { useAppContext } from './AppContext';
import { fmtMoney, itemPaidShare } from '../utils';

// ── ESTIMATORS ────────────────────────────────────────────────────────────────
// Best-effort estimates of how much was paid against this item. Bookkeeper
// can override the amounts in the modal before confirming.

// Model B per-item (2026-06-03): client credit on cancel = actual cash
// received against this item's DEP invoice. Reads payment_allocations
// directly via itemPaidShare. Replaces the pre-Model-B math that derived
// expected-deposit from clientDepositPct(vdp) × clientTotal — same drift
// bug shape as the FIN math (vdp change via CO mid-flight would have
// produced a wrong credit). With itemPaidShare, the credit matches
// real cash regardless of any rule changes.
function estimateClientDeposit(item, invoices, allocations, allItems) {
  if (!item) return 0;
  return itemPaidShare(item, invoices, allocations, allItems);
}

function findItemPO(item, purchaseOrders) {
  return (purchaseOrders || []).find(function(po) {
    return Array.isArray(po.items) && po.items.indexOf(item.id) >= 0;
  });
}

function estimateVendorDeposit(po, vendorPayments) {
  if (!po) return 0;
  var poRef = po.po_ref || '';
  var deposits = (vendorPayments || []).filter(function(p) {
    return p.reference && poRef && String(p.reference).indexOf(poRef) >= 0
        && (p.payment_type === 'Deposit' || p.payment_type === 'Deposit Payment');
  });
  var totalDeposit = deposits.reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  var itemCount = (po.items || []).length || 1;
  return Math.round(totalDeposit / itemCount * 100) / 100;
}

// ── STYLES ────────────────────────────────────────────────────────────────────
var inp = {
  width: '100%', padding: '8px 12px', fontSize: 13, fontFamily: 'inherit',
  border: '0.5px solid #E0E0DC', borderRadius: 6, background: '#fff',
  outline: 'none', boxSizing: 'border-box',
};
var lbl = { fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888', marginBottom: 4 };
var section = { marginBottom: 18, paddingBottom: 18, borderBottom: '0.5px solid #EFEFEC' };
var radio = { marginRight: 8, accentColor: '#111' };
var radioLabel = { display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#333', cursor: 'pointer', padding: '6px 0' };
var btnGhost = { height: 32, padding: '0 16px', borderRadius: 18, border: '0.5px solid #E0E0DC', background: '#fff', color: '#111', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' };
var btnDanger = { height: 32, padding: '0 18px', borderRadius: 18, border: 'none', background: '#C8322A', color: '#fff', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' };

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function CancelItemModal(p) {
  var ctx = useAppContext();
  var items = ctx.items || [];
  var clients = ctx.clients || [];
  var projects = ctx.projects || [];
  var purchaseOrders = ctx.purchaseOrders || [];
  var vendorPayments = ctx.vendorPayments || [];
  var invoices = ctx.invoices || [];
  var paymentAllocations = ctx.paymentAllocations || [];
  var saveClientCreditEntry = ctx.saveClientCreditEntry;
  var saveVendorCreditEntry = ctx.saveVendorCreditEntry;
  var clientCreditLedger = ctx.clientCreditLedger || [];
  var vendorCreditLedger = ctx.vendorCreditLedger || [];
  var updateItem = ctx.updateItem;
  var userEmail = ctx.userEmail || '';

  var item = p.item;

  // Resolve client + PO context
  var project = projects.find(function(pr) { return pr.label === (item.projectLabel || item.project); });
  var client  = project ? clients.find(function(c) { return c.id === project.clientId; }) : null;
  var po      = findItemPO(item, purchaseOrders);

  // Default amount estimates (bookkeeper can override)
  var clientDepositEst = useMemo(function() {
    return estimateClientDeposit(item, invoices, paymentAllocations, items);
  }, [item, invoices, paymentAllocations, items]);
  var vendorDepositEst = useMemo(function() { return estimateVendorDeposit(po, vendorPayments); }, [po, vendorPayments]);

  var hasClientDeposit = clientDepositEst > 0;
  var hasVendorDeposit = vendorDepositEst > 0;

  // Form state
  var cas = useState(hasClientDeposit ? 'hold' : 'none');     var clientAction = cas[0];   var setClientAction = cas[1];
  var cts = useState('');                                      var clientTargetId = cts[0]; var setClientTargetId = cts[1];
  var cms = useState(String(clientDepositEst.toFixed(2)));    var clientAmount = cms[0];   var setClientAmount = cms[1];

  var vas = useState(hasVendorDeposit ? 'hold' : 'none');     var vendorAction = vas[0];   var setVendorAction = vas[1];
  var vts = useState('');                                      var vendorTargetPoId = vts[0]; var setVendorTargetPoId = vts[1];
  var vms = useState(String(vendorDepositEst.toFixed(2)));    var vendorAmount = vms[0];   var setVendorAmount = vms[1];

  var rs = useState('');                                       var reason = rs[0];          var setReason = rs[1];
  var ss = useState('');                                       var err = ss[0];             var setErr = ss[1];
  var sbs = useState(false);                                   var saving = sbs[0];         var setSaving = sbs[1];

  // Assembly peer cascade. If the item being cancelled is part of an
  // assembly, default to cancelling all peers too -- dropping one peer
  // usually breaks the whole assembly (no fabric → workroom can't build).
  // Each peer is independent merchandise (see CLAUDE.md "Component
  // Independence") with its own deposit, so each cancelled peer needs
  // its own credit ledger entry sized to its own deposit -- not one
  // entry summed against the chosen peer.
  var assemblyPeers = useMemo(function() {
    if (!item || !item.assemblyId) return [];
    return items.filter(function(x) { return x.assemblyId === item.assemblyId && x.id !== item.id && x.status !== 'Cancelled'; });
  }, [items, item]);
  var cps = useState(assemblyPeers.length > 0); var cancelPeers = cps[0]; var setCancelPeers = cps[1];

  // Per-peer credit estimates. Each peer that will be cancelled gets a
  // 'hold' (no apply/refund) credit entry sized to its own estimated
  // deposit, on both the client and vendor ledgers. Bookkeeper can later
  // refund or apply these entries from the credit ledger UI.
  var peerCredits = useMemo(function() {
    if (!cancelPeers || assemblyPeers.length === 0) return [];
    return assemblyPeers.map(function(peer) {
      var peerPo = findItemPO(peer, purchaseOrders);
      return {
        item: peer,
        po: peerPo,
        clientDepositEst: estimateClientDeposit(peer, invoices, paymentAllocations, items),
        vendorDepositEst: estimateVendorDeposit(peerPo, vendorPayments),
      };
    });
  }, [cancelPeers, assemblyPeers, purchaseOrders, invoices, paymentAllocations, items, vendorPayments]);

  var peerClientTotal = peerCredits.reduce(function(s, p) { return s + p.clientDepositEst; }, 0);
  var peerVendorTotal = peerCredits.reduce(function(s, p) { return s + p.vendorDepositEst; }, 0);

  // Eligible client targets — same client, not cancelled, not this item, not a child
  var eligibleClientTargets = useMemo(function() {
    if (!client) return [];
    return items.filter(function(it) {
      if (it.id === item.id) return false;
      if (it.parentId) return false;
      if (it.status === 'Cancelled') return false;
      var pj = projects.find(function(pr) { return pr.label === (it.projectLabel || it.project); });
      return pj && pj.clientId === client.id;
    });
  }, [items, item, client, projects]);

  // Eligible vendor targets — same vendor, different PO
  var eligibleVendorTargets = useMemo(function() {
    return purchaseOrders.filter(function(otherPO) {
      if (po && otherPO.id === po.id) return false;
      return otherPO.vendorName === item.vendor;
    });
  }, [purchaseOrders, po, item]);

  async function confirm() {
    setErr('');
    var clientAmt = parseFloat(String(clientAmount).replace(/[$,\s]/g, '')) || 0;
    var vendorAmt = parseFloat(String(vendorAmount).replace(/[$,\s]/g, '')) || 0;

    if (clientAction === 'apply' && !clientTargetId) { setErr('Pick the item to apply the client credit to.'); return; }
    if (vendorAction === 'apply' && !vendorTargetPoId) { setErr('Pick the PO to apply the vendor credit to.'); return; }

    setSaving(true);
    var today = new Date().toISOString().slice(0, 10);
    var itemRef = item.itemNumber || item.item || item.id;
    // Idempotency tag: every ledger row written for this cancel carries
    // CANCEL:<itemId> in its reference -- one tag for the chosen item,
    // one per peer when cancelPeers is on. A retry checks all of these
    // tags and aborts before writing duplicates. Protects against
    // double-clicks and re-opens after a partial failure.
    var idemTag = 'CANCEL:' + item.id;
    var allTagsToCheck = [idemTag];
    if (cancelPeers) {
      peerCredits.forEach(function(pc) { allTagsToCheck.push('CANCEL:' + pc.item.id); });
    }
    var collidingTag = allTagsToCheck.find(function(tag) {
      return clientCreditLedger.some(function(e) { return String(e.reference || '').indexOf(tag) >= 0; })
          || vendorCreditLedger.some(function(e) { return String(e.reference || '').indexOf(tag) >= 0; });
    });
    if (collidingTag) {
      setSaving(false);
      var collidingId = collidingTag.replace('CANCEL:', '');
      var msg = collidingId === item.id
        ? 'This item already has a cancellation on the credit ledger.'
        : 'A peer item (' + collidingId + ') already has a cancellation on the credit ledger.';
      setErr(msg + ' Refresh the page if the status hasn\'t flipped.');
      return;
    }

    try {
      // ── CLIENT SIDE ── Await each ledger write so a DB failure aborts
      // before the item-status flip. Without await, a queued write that
      // never persists would leave the item Cancelled with no credit on
      // the books.
      var clientWrites = [];
      if (hasClientDeposit && clientAmt > 0 && client && clientAction !== 'none') {
        clientWrites.push(saveClientCreditEntry({
          clientId: client.id,
          entryDate: today,
          entryType: 'cancelled_deposit',
          amount: clientAmt,
          sourceItemId: item.id,
          reference: 'Cancelled ' + itemRef + ' [' + idemTag + ']',
          notes: reason,
          createdBy: userEmail,
        }));
        if (clientAction === 'apply' && clientTargetId) {
          var target = items.find(function(it) { return it.id === clientTargetId; });
          clientWrites.push(saveClientCreditEntry({
            clientId: client.id,
            entryDate: today,
            entryType: 'applied_to_item',
            amount: -clientAmt,
            sourceItemId: item.id,
            targetItemId: clientTargetId,
            reference: 'Applied to ' + (target ? (target.itemNumber || target.item || target.id) : '') + ' [' + idemTag + ']',
            notes: reason,
            createdBy: userEmail,
          }));
        } else if (clientAction === 'refund') {
          clientWrites.push(saveClientCreditEntry({
            clientId: client.id,
            entryDate: today,
            entryType: 'refund',
            amount: -clientAmt,
            sourceItemId: item.id,
            reference: 'Cash refund [' + idemTag + ']',
            notes: reason,
            createdBy: userEmail,
          }));
        }
      }

      // ── VENDOR SIDE ──
      var vendorWrites = [];
      if (hasVendorDeposit && vendorAmt > 0 && item.vendor && vendorAction !== 'none') {
        vendorWrites.push(saveVendorCreditEntry({
          vendorName: item.vendor,
          entryDate: today,
          entryType: 'cancelled_deposit',
          amount: vendorAmt,
          sourcePoId: po ? po.id : null,
          sourceItemId: item.id,
          reference: 'Cancelled ' + itemRef + (po ? ' from ' + (po.po_ref || '') : '') + ' [' + idemTag + ']',
          notes: reason,
          createdBy: userEmail,
        }));
        if (vendorAction === 'apply' && vendorTargetPoId) {
          var targetPo = purchaseOrders.find(function(x) { return x.id === vendorTargetPoId; });
          vendorWrites.push(saveVendorCreditEntry({
            vendorName: item.vendor,
            entryDate: today,
            entryType: 'applied_to_po',
            amount: -vendorAmt,
            sourcePoId: po ? po.id : null,
            targetPoId: vendorTargetPoId,
            reference: 'Applied to ' + (targetPo ? targetPo.po_ref || '' : '') + ' [' + idemTag + ']',
            notes: reason,
            createdBy: userEmail,
          }));
        } else if (vendorAction === 'refund') {
          vendorWrites.push(saveVendorCreditEntry({
            vendorName: item.vendor,
            entryDate: today,
            entryType: 'refund',
            amount: -vendorAmt,
            sourcePoId: po ? po.id : null,
            reference: 'Vendor refund [' + idemTag + ']',
            notes: reason,
            createdBy: userEmail,
          }));
        } else if (vendorAction === 'writeoff') {
          vendorWrites.push(saveVendorCreditEntry({
            vendorName: item.vendor,
            entryDate: today,
            entryType: 'writeoff',
            amount: -vendorAmt,
            sourcePoId: po ? po.id : null,
            reference: 'Write-off (non-refundable) [' + idemTag + ']',
            notes: reason,
            createdBy: userEmail,
          }));
        }
      }

      // ── PEER CREDITS ── Each cancelled peer is independent merchandise
      // (CLAUDE.md "Component Independence") with its own deposit, so each
      // peer needs its own credit entry sized to its own deposit estimate.
      // Peer entries default to 'hold' (no apply/refund) -- bookkeeper can
      // refund or apply later from the credit ledger UI. Each peer carries
      // its own idempotency tag CANCEL:<peerId> so a re-run on a single
      // peer separately won't double-write.
      if (cancelPeers && peerCredits.length > 0) {
        peerCredits.forEach(function(pc) {
          var peerIdem = 'CANCEL:' + pc.item.id;
          var peerRef = pc.item.itemNumber || pc.item.item || pc.item.id;
          // Peer client credit (only if there's a deposit estimate and a client)
          if (pc.clientDepositEst > 0 && client) {
            clientWrites.push(saveClientCreditEntry({
              clientId: client.id,
              entryDate: today,
              entryType: 'cancelled_deposit',
              amount: pc.clientDepositEst,
              sourceItemId: pc.item.id,
              reference: 'Cancelled ' + peerRef + ' (peer of ' + itemRef + ') [' + peerIdem + ']',
              notes: reason,
              createdBy: userEmail,
            }));
          }
          // Peer vendor credit (per-peer vendor and PO -- may differ from
          // the chosen item's vendor since each peer is independent)
          if (pc.vendorDepositEst > 0 && pc.item.vendor) {
            vendorWrites.push(saveVendorCreditEntry({
              vendorName: pc.item.vendor,
              entryDate: today,
              entryType: 'cancelled_deposit',
              amount: pc.vendorDepositEst,
              sourcePoId: pc.po ? pc.po.id : null,
              sourceItemId: pc.item.id,
              reference: 'Cancelled ' + peerRef + (pc.po ? ' from ' + (pc.po.po_ref || '') : '') + ' (peer of ' + itemRef + ') [' + peerIdem + ']',
              notes: reason,
              createdBy: userEmail,
            }));
          }
        });
      }

      // Wait for every ledger write to land. If any returned null
      // (DB save failed after retries), abort BEFORE flipping the item
      // status -- credit has to be on the books before we mark the item
      // Cancelled.
      var ledgerResults = await Promise.all(clientWrites.concat(vendorWrites));
      var ledgerFailed = ledgerResults.filter(function(r) { return r === null; }).length;
      if (ledgerFailed > 0) {
        setSaving(false);
        setErr('Credit ledger save failed (' + ledgerFailed + ' of ' + ledgerResults.length + '). Item not cancelled. Try again — the idempotency tag will prevent duplicates.');
        return;
      }

      // Now flip the item (and any peers) to Cancelled. Sequential awaits
      // so a save failure stops the cascade and surfaces.
      var idsToCancel = [item.id];
      if (cancelPeers && item.assemblyId) {
        items.forEach(function(peer) {
          if (peer.assemblyId === item.assemblyId && peer.id !== item.id && !isCancelledItem(peer)) {
            idsToCancel.push(peer.id);
          }
        });
      }
      var statusFailed = [];
      for (var i = 0; i < idsToCancel.length; i++) {
        var ok = await updateItem(idsToCancel[i], { status: 'Cancelled' });
        if (ok === false) statusFailed.push(idsToCancel[i]);
      }
      if (statusFailed.length > 0) {
        setSaving(false);
        setErr('Credit recorded, but ' + statusFailed.length + ' of ' + idsToCancel.length + ' item status updates failed. Refresh and re-cancel just the failed item(s) — the existing credit will be detected and skipped.');
        return;
      }

      setSaving(false);
      if (p.onCancelled) p.onCancelled();
      p.onClose();
    } catch (e) {
      setSaving(false);
      setErr('Failed: ' + (e.message || 'unknown'));
    }
  }
  function isCancelledItem(it) { return !!(it && it.status === 'Cancelled'); }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 560, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#111' }}>Cancel item</div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            {item.itemNumber ? <span style={{ fontFamily: 'monospace' }}>{item.itemNumber} · </span> : null}
            {item.item || item.productName || 'Untitled'}
          </div>
          <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
            {item.vendor || 'No vendor'} · {item.projectLabel || item.project || 'No project'}
            {client ? <span> · {client.name || client.id}</span> : null}
          </div>
        </div>

        {/* Client side */}
        {hasClientDeposit && client ? (
          <div style={section}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#111', marginBottom: 8 }}>
              Client deposit — <span style={{ color: '#1A5C38' }}>{fmtMoney(clientDepositEst)} paid</span>
            </div>
            <div>
              <label style={radioLabel}>
                <input type="radio" name="client" value="apply" checked={clientAction === 'apply'} onChange={function() { setClientAction('apply'); }} style={radio} />
                Apply to another item now
              </label>
              {clientAction === 'apply' && (
                <select value={clientTargetId} onChange={function(e) { setClientTargetId(e.target.value); }} style={Object.assign({}, inp, { marginTop: 4, marginBottom: 8 })}>
                  <option value="">— Select item —</option>
                  {eligibleClientTargets.map(function(it) {
                    return <option key={it.id} value={it.id}>
                      {(it.itemNumber || '—') + ' · ' + (it.item || it.productName || 'Untitled') + ' · ' + (it.projectLabel || it.project || '')}
                    </option>;
                  })}
                </select>
              )}
              <label style={radioLabel}>
                <input type="radio" name="client" value="hold" checked={clientAction === 'hold'} onChange={function() { setClientAction('hold'); }} style={radio} />
                Hold as credit (default — apply later)
              </label>
              <label style={radioLabel}>
                <input type="radio" name="client" value="refund" checked={clientAction === 'refund'} onChange={function() { setClientAction('refund'); }} style={radio} />
                Refund to client (cash)
              </label>
              <label style={radioLabel}>
                <input type="radio" name="client" value="none" checked={clientAction === 'none'} onChange={function() { setClientAction('none'); }} style={radio} />
                No deposit was paid (skip)
              </label>
            </div>
            {clientAction !== 'none' && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: '#888' }}>Amount:</span>
                <input type="text" inputMode="decimal" value={clientAmount} onChange={function(e) { setClientAmount(e.target.value); }} style={Object.assign({}, inp, { width: 140 })} />
              </div>
            )}
          </div>
        ) : null}

        {/* Vendor side */}
        {hasVendorDeposit ? (
          <div style={section}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#111', marginBottom: 8 }}>
              Vendor deposit — <span style={{ color: '#1A5C38' }}>{fmtMoney(vendorDepositEst)} paid to {item.vendor}</span>
              {po ? <span style={{ color: '#888', fontWeight: 400 }}> on {po.po_ref}</span> : null}
            </div>
            <div>
              <label style={radioLabel}>
                <input type="radio" name="vendor" value="apply" checked={vendorAction === 'apply'} onChange={function() { setVendorAction('apply'); }} style={radio} />
                Apply to another PO with this vendor
              </label>
              {vendorAction === 'apply' && (
                <select value={vendorTargetPoId} onChange={function(e) { setVendorTargetPoId(e.target.value); }} style={Object.assign({}, inp, { marginTop: 4, marginBottom: 8 })}>
                  <option value="">— Select PO —</option>
                  {eligibleVendorTargets.map(function(otherPO) {
                    return <option key={otherPO.id} value={otherPO.id}>
                      {(otherPO.po_ref || '—') + ' · ' + (otherPO.project || '')}
                    </option>;
                  })}
                </select>
              )}
              <label style={radioLabel}>
                <input type="radio" name="vendor" value="hold" checked={vendorAction === 'hold'} onChange={function() { setVendorAction('hold'); }} style={radio} />
                Hold as vendor credit
              </label>
              <label style={radioLabel}>
                <input type="radio" name="vendor" value="refund" checked={vendorAction === 'refund'} onChange={function() { setVendorAction('refund'); }} style={radio} />
                Vendor refunded the deposit
              </label>
              <label style={radioLabel}>
                <input type="radio" name="vendor" value="writeoff" checked={vendorAction === 'writeoff'} onChange={function() { setVendorAction('writeoff'); }} style={radio} />
                Write off — vendor won't refund
              </label>
              <label style={radioLabel}>
                <input type="radio" name="vendor" value="none" checked={vendorAction === 'none'} onChange={function() { setVendorAction('none'); }} style={radio} />
                No vendor deposit (skip)
              </label>
            </div>
            {vendorAction !== 'none' && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: '#888' }}>Amount:</span>
                <input type="text" inputMode="decimal" value={vendorAmount} onChange={function(e) { setVendorAmount(e.target.value); }} style={Object.assign({}, inp, { width: 140 })} />
              </div>
            )}
          </div>
        ) : null}

        {/* No-deposit case — just confirm cancellation */}
        {!hasClientDeposit && !hasVendorDeposit ? (
          <div style={section}>
            <div style={{ fontSize: 12, color: '#888', lineHeight: 1.5 }}>
              No deposits have been paid against this item. Cancellation is a clean state change — no ledger entries needed.
            </div>
          </div>
        ) : null}

        {/* Assembly-peer cascade */}
        {assemblyPeers.length > 0 && (
          <div style={{ marginBottom: 18, padding: '10px 12px', background: '#FFF8E1', border: '0.5px solid #F5C800', borderRadius: 6 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={cancelPeers}
                onChange={function(e) { setCancelPeers(e.target.checked); }}
                style={{ marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 12, color: '#111', fontWeight: 600 }}>
                  Also cancel the {assemblyPeers.length} other peer{assemblyPeers.length > 1 ? 's' : ''} in this assembly
                </div>
                <div style={{ fontSize: 11, color: '#8A5400', marginTop: 4 }}>
                  {assemblyPeers.map(function(p) { return (p.childType || 'peer') + (p.itemNumber ? ' (' + p.itemNumber + ')' : ''); }).join(', ')}
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  Default ON because dropping one peer usually means the assembly is gone (no fabric → workroom can't build → hardware has nothing to mount on). Uncheck only if you're keeping the assembly alive and re-speccing just this one peer.
                </div>
                {cancelPeers && peerCredits.length > 0 && (peerClientTotal > 0 || peerVendorTotal > 0) && (
                  <div style={{ fontSize: 11, color: '#111', marginTop: 10, padding: '8px 10px', background: '#fff', border: '0.5px solid #E0E0DC', borderRadius: 4 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Peer credit entries to be written (held on ledger):</div>
                    {peerCredits.map(function(pc) {
                      var label = (pc.item.childType || 'peer') + (pc.item.itemNumber ? ' (' + pc.item.itemNumber + ')' : '');
                      var bits = [];
                      if (pc.clientDepositEst > 0) bits.push('client ' + fmtMoney(pc.clientDepositEst));
                      if (pc.vendorDepositEst > 0) bits.push((pc.item.vendor || 'vendor') + ' ' + fmtMoney(pc.vendorDepositEst));
                      if (bits.length === 0) return null;
                      return (
                        <div key={pc.item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#555' }}>
                          <span>{label}</span>
                          <span>{bits.join(' · ')}</span>
                        </div>
                      );
                    })}
                    <div style={{ fontSize: 10, color: '#888', marginTop: 6, paddingTop: 6, borderTop: '0.5px solid #EFEFEC' }}>
                      Each peer is its own merchandise so each holds its own deposit. Held credits — refund or apply later from the credit ledger.
                    </div>
                  </div>
                )}
              </div>
            </label>
          </div>
        )}

        {/* Reason */}
        <div style={{ marginBottom: 18 }}>
          <div style={lbl}>Reason for cancellation</div>
          <textarea value={reason} onChange={function(e) { setReason(e.target.value); }} rows={2}
            placeholder="e.g. Client changed mind, swapping for new sofa"
            style={Object.assign({}, inp, { resize: 'vertical', minHeight: 56 })} />
        </div>

        {err && <div style={{ marginBottom: 12, padding: '8px 12px', background: '#FDECEA', border: '0.5px solid #F2A39C', color: '#C8322A', fontSize: 11, borderRadius: 6 }}>{err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={btnGhost} disabled={saving} onClick={p.onClose}>Don't cancel</button>
          <button style={btnDanger} disabled={saving} onClick={confirm}>
            {saving ? 'Cancelling…' : 'Confirm Cancellation'}
          </button>
        </div>
      </div>
    </div>
  );
}
